import { DestroyRef, inject, Injectable } from '@angular/core';
import { ActivatedRoute, ActivatedRouteSnapshot } from '@angular/router';
import { Store } from '@ngrx/store';
import { LoggerService } from '@services/logger.service';
import { BehaviorSubject, combineLatest, tap } from 'rxjs';
import { AppState } from 'src/app/reducers';
import { User } from '../models/core.models';
import { selectFeed } from '../reducers';
import { TranslateService } from '@ngx-translate/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Injectable({
  providedIn: 'root',
})
export class PageTitleService {
  private routeTitleItems$ = new BehaviorSubject<string[]>([]);
  private stateTitleItems$ = new BehaviorSubject<string[]>([]);

  private destroyRef = inject(DestroyRef);

  constructor(
    private store: Store<AppState>,
    private translateService: TranslateService,
  ) {}

  subscribeForFeedStateUpdates() {
    this.store
      .select(selectFeed)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        tap((feed) => {
          const currentItems = [];

          if (feed) {
            const pageTitleItem = feed.title;
            currentItems.push(pageTitleItem);
          }

          this.stateTitleItems$.next(currentItems);
        }),
      )
      .subscribe();
  }

  updatePageTitle(activatedRoute: ActivatedRoute, user: User) {
    const pageTitles: string[] = ['Sensika'];

    let currentRoute = activatedRoute.root;
    while (currentRoute.firstChild) {
      currentRoute = currentRoute.firstChild;
      const { path, data: routeConfigData } = currentRoute.routeConfig;
      const { params, data } = currentRoute.snapshot;

      if (routeConfigData?.['pageTitle']) {
        let pageTitle: string = routeConfigData['pageTitle'];
        let label = '';
        if (pageTitle?.startsWith('resolve:')) {
          const resolvePattern = pageTitle.split(':')[1];
          const [objectName, propertyName] = resolvePattern.split('.');

          // Retrieve the resolved object using the object name
          const resolvedObject = currentRoute.snapshot.data[objectName];
          label = resolvedObject?.[propertyName] || 'Unknown';
        } else {
          this.addPageTitleItem(
            pageTitles,
            this.translateService.instant(pageTitle),
          );
        }
        if (label.length > 0) {
          this.addPageTitleItem(pageTitles, label);
        }
      } else {
        if (path) {
          if (data['workspace']) {
            this.addPageTitleItem(pageTitles, data['workspace'].title);
          }
          if (params['feedId'] && data['feed']) {
            this.addFeedPageTitle(pageTitles, currentRoute.snapshot);
          } else if (path === 'connector' || params['connectorId']) {
            this.addConnectorPageTitle(pageTitles, currentRoute.snapshot);
          }
        }
      }
    }

    this.setItems(pageTitles);
  }

  setItems(items: string[]) {
    this.routeTitleItems$.next(items);
  }

  addPageTitleItem(pageTitles: string[], item: string): void {
    pageTitles.push(item);
  }

  addFeedPageTitle(pageTitles: string[], route: ActivatedRouteSnapshot) {
    const feed = route.data['feed'];
    const pageTitle = feed.title;

    pageTitles.push(pageTitle);
  }

  addConnectorPageTitle(pageTitles: string[], route: ActivatedRouteSnapshot) {
    const label = route.params['connectorId']
      ? route.data['connector'].title
      : this.translateService.instant('page-title.title.connectors');
    pageTitles.push(label);
  }

  getRouterItems() {
    return this.routeTitleItems$.asObservable();
  }
}
